import type { AxiosResponse, AxiosError } from 'axios'
import Router from 'next/router'

import { windowLocation } from '@front/logic/others/windowLocation'
import type { InterceptorErrorData } from '@front/type/axios'

import { forbiddenError, unauthorizedErrors } from '../../../../others'
import { workerLoginUrl } from '../../../../bff-worker/config'
import { logoutUser } from '../../../index'
import { refreshToken } from '../../auth/refreshToken'
import { AxiosInstance } from '../../axios.instance'

export const ROUTE_FORBIDDEN_ERROR = '/error/forbidden'
const DO_NOT_REDIRECT_ROUTES = [
  ROUTE_FORBIDDEN_ERROR,
  '/login',
  '/mobile',
  '/onboarding/signup',
  'reset-password/get-code',
]

export const beforeReceiveError = async (
  error: AxiosError<InterceptorErrorData>,
): Promise<AxiosResponse<unknown>> => {
  const errorStatusCode = error?.response?.status

  if (!errorStatusCode) {
    return Promise.reject(error)
  }

  const isTokenRefreshNeeded =
    errorStatusCode === forbiddenError &&
    (error.response?.data === 'Token expired' ||
      error.response?.data?.message === 'Token expired' ||
      error.message === 'Token expired')

  if (isTokenRefreshNeeded) {
    const hasSucceeded = await refreshToken()
    if (hasSucceeded) {
      return AxiosInstance(error.config || {})
    }
  }

  // If the error comes from login endpoint, we make some cleaning
  if (error.config?.url === workerLoginUrl) {
    await logoutUser({
      isForcedLogout: true,
      shouldRedirectToWelcomePage: false,
    })
    return Promise.reject(error)
  }

  // We don't redirect the user to the forbidden page if the user is on some specific pages
  if (
    DO_NOT_REDIRECT_ROUTES.some((route) => windowLocation.href.endsWith(route))
  ) {
    return Promise.reject(error)
  }

  // We redirect the user to the forbidden page only for 403 errors
  if (errorStatusCode === forbiddenError) {
    await Router.push(
      {
        pathname: ROUTE_FORBIDDEN_ERROR,
        query: {
          redirect: windowLocation.href,
        },
      },
      ROUTE_FORBIDDEN_ERROR,
    )
  }

  // We logout and redirect the user to the login page only for 401 and 440 errors
  if (unauthorizedErrors.includes(errorStatusCode)) {
    await logoutUser({
      isForcedLogout: true,
      shouldRedirectToWelcomePage: false,
      redirect: windowLocation.href,
    })
  }

  return Promise.reject(error)
}
